// eslint-disable-next-line no-unused-vars
import moment from 'moment-timezone'; // DO NOT REMOVE THIS, IT WILL BREAK THE APP IF REMOVED

import { useEffect, useMemo } from 'react';
import { ApolloProvider } from '@apollo/client';
import { Provider as ReduxProvider } from 'react-redux';
import { Redirect, Route, BrowserRouter as Router, Switch } from 'react-router-dom';
import { GrowthBook, GrowthBookProvider } from '@growthbook/growthbook-react';
import PrivateRoute from './components/shared/PrivateRoute';
import { PERMISSIONS } from './helpers/api/auth/permissions';
import { apolloClient } from './lib/api/client';
import store from './redux/store';
import { useThemeMode } from './hooks';
import config from './config';

import DriversApproval from './pages/DriversApproval';
import Home from './pages/Home';
import Login from './pages/Login';
import Location from './pages/Location';
import RideSimPage from './pages/Rides/simulator/RideSimPage';
import Stations from './pages/Stations';
import Trips from './pages/Trips';
import Users from './pages/Users';
import UserProfile from './pages/UserProfile';
import Rides from './pages/Rides';
import FleetManagement from './pages/FleetManagement';
import Notifications from './pages/Notifications';
import Promotions from './pages/Promotions';
import TripThrottling from './pages/TripThrottling';
import RiderDriverExclusions from './pages/RiderDriverExclusions';
import SupplyDemandPace from './pages/SupplyDemandPace';
import DriverProfile from './pages/DriverProfile';
import Drivers from './pages/Drivers';
import Vehicles from './pages/Vehicles';
import DriverQuests from './pages/DriverQuests';

import { ToastProvider } from './hooks/useToast';
import { CssBaseline } from './ui/core';
import { ThemeProvider, buildTheme } from './ui/styles';
import { QueryClient, QueryClientProvider as TanStackQueryClientProvider } from '@tanstack/react-query';

const reactQueryClient = new QueryClient();

// Module-scope instance (mirrors `reactQueryClient` above) so the SDK client
// and its cached features survive re-renders instead of re-fetching on every
// one, the way an ad-hoc `new GrowthBook()` per-render would.
const growthbook = new GrowthBook({
  apiHost: config.growthbook.apiHost,
  clientKey: config.growthbook.clientKey,
  subscribeToChanges: true,
});

function AppRoutes() {
  return (
    <Router>
      <Switch>
        <Route exact path="/login" component={Login} />
        <PrivateRoute path="/home" component={Home} />
        {/* One page per person — Account / Rider / Driver. Registered ABOVE /users so
            the more specific path wins, and outside the Users shell because its Driver
            tab opens a document inspector that needs the full width the People rail
            was taking. No permission on the route: support needs the user page. The
            Driver TAB carries MANAGE_DRIVERS instead, since that is where the document
            decisions and standing actions live. */}
        <PrivateRoute exact path="/users/:id" component={UserProfile} />
        <PrivateRoute path="/users" component={Users} />
        <PrivateRoute path="/trips" component={Trips} />
        <PrivateRoute path="/rides" component={Rides} />
        {/* The live ride page driven by a simulated stream, for judging the map
            and the car's motion without a real ride. Behind the same login as
            everything else: it holds no real data, but an unauthenticated page
            is a page anyone can open. */}
        <PrivateRoute exact path="/dev/ride-sim" component={RideSimPage} />
        <PrivateRoute path="/drivers-approval" component={DriversApproval} />
        <PrivateRoute path="/stations" component={Stations} />
        <PrivateRoute path="/location" component={Location} />
        <PrivateRoute path="/fleet-management" component={FleetManagement} />
        <PrivateRoute path="/notifications" component={Notifications} />
        <PrivateRoute path="/promotions" component={Promotions} />
        {/* Registered unconditionally — the `admin-trip-throttling` flag gates
            the page's own content (rendering a 404 when off), not the route
            itself. Gating the route here would make a flagged-off deep-link
            fall through to the trailing catch-all and silently redirect to
            /home instead of 404ing, which acceptance forbids. */}
        <PrivateRoute path="/trip-throttling" component={TripThrottling} />
        {/* Registered unconditionally — the `admin-rider-driver-exclusions`
            flag gates the page's own content (rendering a 404 when off), not
            the route itself, for the same reason /trip-throttling above is
            unconditional. No permission on the route: any admin may manage
            exclusions for v1. */}
        <PrivateRoute path="/rider-driver-exclusions" component={RiderDriverExclusions} />
        {/* Read-only, open to all admins, no flag: unlike the two routes
            above, there's no gate on this page's own content either. */}
        <PrivateRoute path="/driver-quests" component={DriverQuests} />
        {/* Supply-Demand Pace (HITCH-5500): status, guards, city-day state,
            readouts and policy changes for the driver-pay controller. Any
            admin, like /promotions. */}
        <PrivateRoute path="/supply-demand-pace" component={SupplyDemandPace} />
        {/* The pre-approved make/model/year catalog. Its own tab, not under
            Fleet: Fleet is Hitch-owned vehicles, this is the reference table
            every driver_vehicle points at — a bad row here is wrong for every
            driver holding it, which is why it has its own permission.

            Enforced on the ROUTE, unlike /trip-throttling above. That one has to
            404 a flagged-off deep link; this one is an entitlement, so an
            unentitled user is better sent somewhere they can use. */}
        <PrivateRoute permission={PERMISSIONS.MANAGE_VEHICLE_CATALOG} path="/vehicles" component={Vehicles} />
        {/* The driver lists and a driver's profile. Additive: /drivers-approval
            is untouched and still its own section. Its own permission rather than
            CHANGE_DRIVER_STATUS: the profile sets documents and standing, which
            governs whether an already-approved driver keeps driving, where the
            funnel only moves applicants. */}
        <PrivateRoute permission={PERMISSIONS.MANAGE_DRIVERS} exact path="/drivers" component={Drivers} />
        <PrivateRoute
          permission={PERMISSIONS.MANAGE_DRIVERS}
          path="/driver-profile/:driverId"
          component={DriverProfile}
        />
        {/* No /prototype/* routes. The mock folders stay as the design record — see
            their NOTES.md — but they are unauthenticated full-screen pages with no
            permission check, so routing to them would serve throwaway mocks publicly
            and pull them into the production bundle. Add a route locally to look at
            one; do not commit it. */}
        <Redirect to="/home" />
      </Switch>
    </Router>
  );
}

function App() {
  const { resolvedMode } = useThemeMode();
  const theme = useMemo(() => buildTheme(resolvedMode), [resolvedMode]);

  useEffect(() => {
    // Fire-and-forget: warms up the SDK's feature cache (for DashoardLayout's
    // nav item, etc.) without gating the rest of the app on it. A short
    // timeout keeps a slow/unreachable `cdn.growthbook.io` (or production's
    // intentionally-empty clientKey - see config/production.js) from ever
    // being able to hang anything; routes that don't read a flag are
    // unaffected either way, and /trip-throttling awaits its own readiness
    // locally (see pages/TripThrottling/index.js) instead of relying on this.
    growthbook.loadFeatures({ timeout: 2000 });
  }, []);

  return (
    <TanStackQueryClientProvider client={reactQueryClient}>
      <ReduxProvider store={store}>
        <ThemeProvider theme={theme}>
          <ApolloProvider client={apolloClient}>
            <ToastProvider>
              <GrowthBookProvider growthbook={growthbook}>
                <CssBaseline />
                <AppRoutes />
              </GrowthBookProvider>
            </ToastProvider>
          </ApolloProvider>
        </ThemeProvider>
      </ReduxProvider>
    </TanStackQueryClientProvider>
  );
}

export default App;
